/* eslint-disable no-restricted-imports */
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  jssPreset,
  ThemeProvider as MuiV4ThemeProvider,
  StylesProvider,
} from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import CssBaselineV5 from '@mui/material/CssBaseline';
import { ThemeProvider as MuiV5ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { SnackbarProvider } from 'notistack';
import { createRoot } from 'react-dom/client';
import { PopupProvider } from 'react-popup-manager';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import SnackbarInitializer from 'components/SnackbarInitializer';
import 'constants/ckeditor';
import ConfiguredAuth0Provider from 'modules/Auth0/ConfiguredAuth0Provider';
import ShareWindowAuth0 from 'modules/Auth0/ShareWindowAuth0';
import { NotificationProvider } from 'modules/Notifications';
import { queryClient } from 'reactQuery/queryClient';
import { muiThemeV4 } from 'theme/muiThemeV4';
import { muiThemeV5 } from 'theme/muiThemeV5';
import 'utils/DataGrid/muiXLicense';
import { msalConfig } from 'utils/auth/msAuthConfig';

import ScrollToTop from './App/components/ScrollToTop';
import App from './App/index';
import config from './config';
import './lib/axios';
import * as serviceWorker from './serviceWorker';
import history from './store/history';
import { persistor, store } from './store/store';

import './app.css';
import './assets/scss/style.scss'; /** legacy styles */

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const root = createRoot(document.getElementById('root'));

const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.initialize();

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AppWithProviders = () => (
  <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <MuiV4ThemeProvider theme={muiThemeV4}>
          <StylesProvider jss={jss}>
            <CssBaseline />
            <MuiV5ThemeProvider theme={muiThemeV5}>
              <CssBaselineV5 />
              <SnackbarProvider dense={true} maxSnack={3}>
                <SnackbarInitializer />
                <QueryClientProvider client={queryClient}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MsalProvider instance={msalInstance}>
                      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                        <ConfiguredAuth0Provider>
                          <ShareWindowAuth0>
                            <NotificationProvider>
                              <Router
                                basename={config.basename}
                                forceRefresh={true}
                                history={history}
                              >
                                <ScrollToTop>
                                  <PopupProvider>
                                    <App />
                                  </PopupProvider>
                                </ScrollToTop>
                              </Router>
                            </NotificationProvider>
                          </ShareWindowAuth0>
                        </ConfiguredAuth0Provider>
                      </GoogleOAuthProvider>
                    </MsalProvider>
                  </LocalizationProvider>
                  <ReactQueryDevtools />
                </QueryClientProvider>
              </SnackbarProvider>
            </MuiV5ThemeProvider>
          </StylesProvider>
        </MuiV4ThemeProvider>
      </PersistGate>
    </Provider>
);

root.render(<AppWithProviders />);

serviceWorker.unregister();
